import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { utils, writeFileXLSX } from "xlsx";
import AuthService from "../services/auth.service";
import DataService from "../services/data.service";
import {
  buildReportSummary,
  formatCurrency,
  formatDateLabel,
  getStatusMeta,
  toInputDateValue,
} from "../common/reporting";

const DisbursementsReport = () => {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [fromDate, setFromDate] = useState(toInputDateValue(monthStart));
  const [toDate, setToDate] = useState(toInputDateValue(today));
  const [tableRows, setTableRows] = useState([]);
  const [retryIds, setRetryIds] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [details, setDetails] = useState({});
  const [projects, setProjects] = useState([]);
  const [searchPhoneNumber, setSearchPhoneNumber] = useState("");
  const [projectId, setProjectId] = useState("0");

  const user = AuthService.getCurrentUser();
  const clientId = user && user.clients && user.clients[0] && user.clients[0].companyCode;
  const clientPrimaryKeyId = user && user.clients && user.clients[0] && user.clients[0].id;

  useEffect(() => {
    if (!clientPrimaryKeyId) {
      return;
    }

    DataService.fetchProjects(clientPrimaryKeyId).then((response) => {
      setProjects([{ id: 0, projectName: "All projects" }, ...response.data]);
    });
  }, [clientPrimaryKeyId]);

  const exportToCSV = () => {
    const worksheet = utils.json_to_sheet(tableRows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Disbursements");
    writeFileXLSX(workbook, "disbursements-report.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF("portrait", "pt", "A4");
    doc.setFontSize(15);
    doc.text("Disbursements Report", 40, 40);
    doc.autoTable({
      startY: 55,
      head: [["Phone Number", "Amount", "Customer Name", "Status", "Mpesa Ref", "Date"]],
      body: tableRows.map((row) => [
        row.phoneNumber,
        formatCurrency(row.amount),
        row.customerName,
        getStatusMeta(row.status, row.statusMessage).label,
        row.mpesaRef,
        formatDateLabel(row.transactionDate),
      ]),
    });
    doc.save("disbursements-report.pdf");
  };

  const loadTransactions = () => {
    const searchPhone = searchPhoneNumber !== "" ? searchPhoneNumber : "all";

    DataService.fetchDisbursementReport(fromDate, toDate, clientId, searchPhone, projectId).then(
      (response) => {
        setTableRows(response.data);
        setRetryIds([]);
      }
    );
  };

  const handleRetryTransactions = () => {
    DataService.retryTransactions(retryIds).then(() => {
      loadTransactions();
    });
  };

  const summary = useMemo(() => buildReportSummary(tableRows), [tableRows]);

  if (!clientId) {
    return (
      <section className="empty-state empty-state--large">
        <span className="bi bi-building-exclamation" aria-hidden="true" />
        <h4>No client profile linked</h4>
        <p>Contact an administrator before accessing transaction reporting.</p>
      </section>
    );
  }

  return (
    <div className="portal-page-stack">
      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Filters</p>
            <h4>Investigate transaction activity</h4>
          </div>
          <div className="button-row">
            <Button variant="outline-primary" onClick={exportToCSV}>
              Export Excel
            </Button>
            <Button variant="outline-primary" onClick={exportPDF}>
              Export PDF
            </Button>
          </div>
        </div>
        <div className="portal-form-grid portal-form-grid--report">
          <Form.Group controlId="reportFromDate">
            <Form.Label>From date</Form.Label>
            <Form.Control type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </Form.Group>
          <Form.Group controlId="reportToDate">
            <Form.Label>To date</Form.Label>
            <Form.Control type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </Form.Group>
          <Form.Group controlId="reportPhoneNumber">
            <Form.Label>Phone number</Form.Label>
            <Form.Control
              type="text"
              value={searchPhoneNumber}
              onChange={(event) => setSearchPhoneNumber(event.target.value)}
              placeholder="Search a specific recipient"
            />
          </Form.Group>
          <Form.Group controlId="reportProject">
            <Form.Label>Project</Form.Label>
            <Form.Control as="select" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              {projects.map((project) => (
                <option value={project.id} key={project.id}>
                  {project.projectName}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </div>
        <div className="button-row mt-4">
          <Button variant="primary" onClick={loadTransactions}>
            Fetch transactions
          </Button>
          <Button variant="outline-secondary" onClick={handleRetryTransactions} disabled={!retryIds.length}>
            Retry selected ({retryIds.length})
          </Button>
        </div>
      </section>

      <section className="metrics-grid">
        <article className="metric-card">
          <p className="metric-card__label">Successful</p>
          <h4>{summary.successful}</h4>
          <span>Transactions completed successfully in the current result set.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Exceptions</p>
          <h4>{summary.failed}</h4>
          <span>Transactions requiring investigation, correction, or retry.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Payments</p>
          <h4>{formatCurrency(summary.payments)}</h4>
          <span>Successful payment value returned by the selected filters.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Charges</p>
          <h4>{formatCurrency(summary.charges)}</h4>
          <span>Total charges associated with successful payment instructions.</span>
        </article>
      </section>

      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Results</p>
            <h4>Disbursement activity</h4>
          </div>
          <span className="portal-pill portal-pill--outline">{tableRows.length} transactions</span>
        </div>
        {tableRows.length ? (
          <div className="table-responsive portal-table-wrap">
            <table className="table portal-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Phone number</th>
                  <th>Amount</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Mpesa ref</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Details</th>
                  <th>Retry</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => {
                  const statusMeta = getStatusMeta(row.status, row.statusMessage);
                  const retryAllowed =
                    (row.transactionType === "Payment" && Number(row.status) === 4) ||
                    Number(row.status) === 8;

                  return (
                    <tr key={row.uniqueId}>
                      <td>{row.phoneNumber}</td>
                      <td>{formatCurrency(row.amount)}</td>
                      <td>{row.customerName}</td>
                      <td>
                        <span className={`status-badge status-badge--${statusMeta.tone}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td>{row.mpesaRef || "—"}</td>
                      <td>{row.transactionType}</td>
                      <td>{formatDateLabel(row.transactionDate)}</td>
                      <td>{row.projectName}</td>
                      <td>
                        <Button
                          variant="link"
                          className="table-link-button"
                          onClick={() => {
                            setShowDetail(true);
                            setDetails(row);
                          }}
                        >
                          View
                        </Button>
                      </td>
                      <td>
                        {retryAllowed ? (
                          <Form.Check
                            type="checkbox"
                            checked={retryIds.includes(row.uniqueId)}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setRetryIds([...retryIds, row.uniqueId]);
                              } else {
                                setRetryIds(retryIds.filter((retryId) => retryId !== row.uniqueId));
                              }
                            }}
                          />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <span className="bi bi-bar-chart" aria-hidden="true" />
            <h5>No transactions loaded</h5>
            <p>Run a search to populate the reporting table and explore transaction outcomes.</p>
          </div>
        )}
      </section>

      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Transaction details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="detail-list">
            {[
              ["Customer name", details.customerName],
              ["Amount", formatCurrency(details.amount)],
              ["Charges", formatCurrency(details.chargeAmount)],
              ["Batch reference", details.batchRef],
              ["Conversation ID", details.conversationId],
              ["Description", details.description],
              ["Mpesa reference", details.mpesaRef],
              ["Originator conversation ID", details.originatorConversationId],
              ["Phone number", details.phoneNumber],
              ["Response description", details.responseDescription],
              ["Result code", details.resultCode],
              ["Result description", details.resultDesc],
              ["Status message", details.statusMessage],
              ["Transaction date", formatDateLabel(details.transactionDate)],
              ["Transaction type", details.transactionType],
              ["Unique ID", details.uniqueId],
              ["Retries", details.retries],
            ].map(([label, value]) => (
              <div className="detail-list__row" key={label}>
                <span>{label}</span>
                <strong>{value || "—"}</strong>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DisbursementsReport;
